import {Component, Input, OnInit} from '@angular/core';
import {UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';

interface DynamicFeeOption
{
  label: string;
  value: string;
}

@Component({
  selector: 'auto-dynamic-fees-table',
  templateUrl: './dynamic-fees-table.component.html',
  styleUrls: ['./dynamic-fees-table.component.scss'],
  standalone: false
})
export class DynamicFeesTableComponent implements OnInit
{
  @Input() feesConfigurationFormGroup = new UntypedFormGroup({});
  @Input() categoryName = '';

  feeTypeOptions: DynamicFeeOption[] = [
    {value: 'documentFee', label: 'Document Fee'},
    {value: 'licenseFee', label: 'License Fee'},
    {value: 'titleFee', label: 'Title Fee'},
    {value: 'acquisitionFee', label: 'Acquisition Fee'},
    {value: 'taxes', label: 'Taxes'},
    {value: 'dispositionFee', label: 'Disposition Fee'},
    {value: 'customFee', label: 'Other / Custom'}
  ];
  purchaseBehaviorOptions: DynamicFeeOption[] = [
    {value: 'includeInSalePrice', label: 'Included in Price'},
    {value: 'addToSalePrice', label: 'Add to Price'},
    {value: 'notUsed', label: 'Not Used'}
  ];
  paymentBehaviorOptions: DynamicFeeOption[] = [
    {value: 'includeInPayment', label: 'Add to Selling Price'},
    {value: 'dueAtSigning', label: 'Due at Signing'},
    {value: 'notUsed', label: 'Not Used'}
  ];
  financeBehaviorOptions: DynamicFeeOption[] = [
    {value: 'includeInPayment', label: 'Add to Selling Price'},
    {value: 'notUsed', label: 'Not Used'}
  ];
  readonly feeTitleMaxLength = 255;

  private readonly customFeeType = 'customFee';
  private readonly taxesFeeType = 'taxes';
  private readonly documentFeeType = 'documentFee';
  private readonly purchaseBehaviorNotUsed = 'notUsed';
  private readonly paymentBehaviorIncludeInPayment = 'includeInPayment';

  /**
   * @param formBuilder
   */
  constructor(private formBuilder: UntypedFormBuilder)
  {
  }

  ngOnInit(): void
  {
    this.initializeFeeRows();
  }

  /**
   * Get Fee Rows
   */
  get feeRows(): UntypedFormArray
  {
    return this.feesConfigurationFormGroup.get('rows') as UntypedFormArray;
  }

  /**
   * Add Fee Row action
   */
  addFeeRow(): void
  {
    const feeRow = this.buildFeeRowFormGroup();

    this.feeRows.push(feeRow);
    this.syncSortOrder();
    this.applyRowState(feeRow);
    this.feesConfigurationFormGroup.markAsDirty();
  }

  /**
   * Remove fee row
   * @param index
   */
  removeFeeRow(index: number): void
  {
    const feeRow = this.getFeeRow(index);

    if (!this.isDefaultDocumentFeeRow(feeRow)) {
      this.feeRows.removeAt(index);
      this.syncSortOrder();
      this.feesConfigurationFormGroup.markAsDirty();
    }
  }

  /**
   * Handle fee type change
   * @param index
   */
  handleFeeTypeChange(index: number): void
  {
    const feeRow = this.getFeeRow(index);

    this.applyRowState(feeRow);
    this.feesConfigurationFormGroup.markAsDirty();
  }

  /**
   * @param index
   */
  canRemoveRow(index: number): boolean
  {
    let canRemoveRow = true;
    const feeRow = this.getFeeRow(index);

    if (this.isDefaultDocumentFeeRow(feeRow)) {
      canRemoveRow = false;
    }

    return canRemoveRow;
  }

  /**
   * @param index
   */
  getValueLabel(index: number): string
  {
    let valueLabel = 'Value ($)';
    const feeRow = this.getFeeRow(index);
    const feeType = this.getRowControlValue(feeRow, 'type');

    if (feeType === this.taxesFeeType) {
      valueLabel = 'Value (%)';
    }

    return valueLabel;
  }

  trackByIndex(index: number): number
  {
    return index;
  }

  /**
   * @private
   */
  private buildFeeRowFormGroup(): UntypedFormGroup
  {
    const accountBusinessCategoryGuid = this.getAccountBusinessCategoryGuid();
    const defaultFeeType = this.getDefaultFeeType();

    return this.formBuilder.group({
      id: new UntypedFormControl(null),
      type: new UntypedFormControl(defaultFeeType),
      customFeeName: new UntypedFormControl(null),
      value: new UntypedFormControl(null),
      isPercent: new UntypedFormControl(false),
      purchaseBehavior: new UntypedFormControl(this.purchaseBehaviorNotUsed),
      purchaseDisplayName: new UntypedFormControl(null),
      leaseBehavior: new UntypedFormControl(this.paymentBehaviorIncludeInPayment),
      financeBehavior: new UntypedFormControl(this.paymentBehaviorIncludeInPayment),
      isDefaultDocumentFee: new UntypedFormControl(false),
      sortOrder: new UntypedFormControl(this.feeRows.length),
      enabled: new UntypedFormControl(true),
      accountBusinessCategoryGuid: new UntypedFormControl(accountBusinessCategoryGuid)
    }) as UntypedFormGroup;
  }

  /**
   * @private
   */
  private getDefaultFeeType(): string
  {
    let defaultFeeType = this.customFeeType;

    this.feeTypeOptions.forEach((feeTypeOption) => {
      if (defaultFeeType === this.customFeeType && feeTypeOption.value !== this.customFeeType) {
        defaultFeeType = feeTypeOption.value;
      }
    });

    return defaultFeeType;
  }

  /**
   * @private
   */
  private initializeFeeRows(): void
  {
    this.feeRows.controls.forEach((feeRowControl) => {
      const feeRow = feeRowControl as UntypedFormGroup;

      this.applyRowState(feeRow);
    });
  }

  /**
   * @private
   */
  private getAccountBusinessCategoryGuid(): string | null
  {
    let accountBusinessCategoryGuid = null;
    const documentFeeGroup = this.feesConfigurationFormGroup.get('documentFee') as UntypedFormGroup | null;

    if (documentFeeGroup !== null) {
      const guidValue = documentFeeGroup.get('accountBusinessCategoryGuid')?.value;

      if (typeof guidValue === 'string' && guidValue !== '') {
        accountBusinessCategoryGuid = guidValue;
      }
    }

    return accountBusinessCategoryGuid;
  }

  /**
   * @private
   */
  private syncSortOrder(): void
  {
    this.feeRows.controls.forEach((feeRowControl, index) => {
      const feeRow = feeRowControl as UntypedFormGroup;

      feeRow.get('sortOrder')?.setValue(index, {emitEvent: false});
    });
  }

  /**
   * @param feeRow
   * @private
   */
  private applyRowState(feeRow: UntypedFormGroup): void
  {
    this.applyDocumentFeeProtection(feeRow);
    this.applyPurchaseDisplayNameValidation(feeRow);
    this.applyPercentState(feeRow);
  }

  /**
   * @param feeRow
   * @private
   */
  private applyPurchaseDisplayNameValidation(feeRow: UntypedFormGroup): void
  {
    const purchaseDisplayNameControl = feeRow.get('purchaseDisplayName') as UntypedFormControl;
    const feeType = this.getRowControlValue(feeRow, 'type');
    const titleValidators = [Validators.maxLength(this.feeTitleMaxLength)];

    if (feeType === this.customFeeType) {
      titleValidators.push(Validators.required, Validators.pattern(/\S/));
    }

    purchaseDisplayNameControl.setValidators(titleValidators);
    purchaseDisplayNameControl.updateValueAndValidity({emitEvent: false});
  }

  /**
   * @param feeRow
   * @private
   */
  private applyPercentState(feeRow: UntypedFormGroup): void
  {
    let isPercent = false;
    const feeType = this.getRowControlValue(feeRow, 'type');

    if (feeType === this.taxesFeeType) {
      isPercent = true;
    }

    feeRow.get('isPercent')?.setValue(isPercent, {emitEvent: false});
  }

  /**
   * @param feeRow
   * @private
   */
  private applyDocumentFeeProtection(feeRow: UntypedFormGroup): void
  {
    const typeControl = feeRow.get('type');
    const isDefaultDocumentFee = this.isDefaultDocumentFeeRow(feeRow);

    if (isDefaultDocumentFee) {
      typeControl?.setValue(this.documentFeeType, {emitEvent: false});
      typeControl?.disable({emitEvent: false});
      feeRow.get('enabled').setValue(true);
    }

    if (!isDefaultDocumentFee) {
      typeControl?.enable({emitEvent: false});
    }
  }

  /**
   * @param index
   * @private
   */
  private getFeeRow(index: number): UntypedFormGroup
  {
    return this.feeRows.at(index) as UntypedFormGroup;
  }

  /**
   * @param feeRow
   * @private
   */
  private isDefaultDocumentFeeRow(feeRow: UntypedFormGroup): boolean
  {
    let isDefaultDocumentFeeRow = false;
    const defaultFlag = feeRow.get('isDefaultDocumentFee')?.value;

    if (defaultFlag === true) {
      isDefaultDocumentFeeRow = true;
    }

    return isDefaultDocumentFeeRow;
  }

  /**
   * @param feeRow
   * @param controlName
   * @private
   */
  private getRowControlValue(feeRow: UntypedFormGroup, controlName: string): string
  {
    let controlValue = '';
    const rawValue = feeRow.get(controlName)?.value;

    if (typeof rawValue === 'string') {
      controlValue = rawValue;
    }

    return controlValue;
  }
}
